// Apolo IDE — GrepResults (Sprint v6 B2).
//
// Lets the user run a ripgrep query against the active session's worktree.
// Backend (code-ide-grep.js) validates pattern + path safety then returns
// a canonical rg command for the Electron daemon to execute.

import { useState } from 'react';
import { Search, FileCode } from 'lucide-react';
import { apiFetch } from '../../../lib/api.js';

export default function GrepResults({ sessionId }) {
  const [pattern, setPattern] = useState('');
  const [isRegex, setIsRegex] = useState(false);
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  async function run(e) {
    e?.preventDefault();
    const q = pattern.trim();
    if (!q) return;
    setRunning(true);
    setError(null);
    const res = await apiFetch('/api/code-ide/grep', {
      method: 'POST',
      body: JSON.stringify({ pattern: q, isRegex, sessionId, maxResults: 100 }),
    });
    setRunning(false);
    if (!res.ok) { setError(res.error || 'Falha no grep'); return; }
    setResults(Array.isArray(res.data?.matches) ? res.data.matches : []);
  }

  return (
    <div className="flex h-full flex-col gap-3 px-3 py-3">
      <form onSubmit={run} className="flex flex-col gap-2">
        <label htmlFor="grep-pattern" className="text-xs uppercase tracking-widest text-apolo-textTertiary">
          Buscar no worktree
        </label>
        <div className="flex gap-2">
          <input
            id="grep-pattern"
            type="text"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="Buscar texto ou regex (ex.: useStreamingChat)"
            className="flex-1 rounded-apolo-md border border-apolo-border bg-apolo-surface2 px-2 py-1.5 text-sm text-apolo-textPrimary placeholder:text-apolo-textPlaceholder focus:border-apolo-accent focus:outline-none"
          />
          <button
            type="submit"
            disabled={running || !pattern.trim()}
            className="inline-flex items-center gap-1 rounded-apolo-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-apolo-bg hover:bg-apolo-accentHover disabled:opacity-50 min-h-touch"
          >
            <Search size={12} /> {running ? 'Buscando…' : 'Buscar'}
          </button>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-apolo-textTertiary">
          <input type="checkbox" checked={isRegex} onChange={(e) => setIsRegex(e.target.checked)} />
          Tratar como regex
        </label>
      </form>

      {error && (
        <div role="alert" className="rounded-apolo-md border border-apolo-error/40 bg-apolo-errorBg p-2 text-xs text-apolo-error">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {results.length === 0 && !running ? (
          <div className="rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 p-4 text-center text-xs text-apolo-textTertiary">
            Nenhum resultado ainda. Digite um padrão e clique buscar.
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {results.map((m, i) => (
              <li key={`${m.path}:${m.line}-${i}`} className="rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2">
                <div className="flex items-center gap-2 text-xs text-apolo-textTertiary">
                  <FileCode size={12} aria-hidden="true" />
                  <span className="truncate">{m.path}</span>
                  <span className="ml-auto">linha {m.line}</span>
                </div>
                <pre className="mt-1 overflow-x-auto text-xs text-apolo-textPrimary">{m.text}</pre>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

GrepResults.defaultProps = { sessionId: null };
